import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Breadcrumb from "@/components/base/Breadcrumb";
import MovieCard from "@/components/base/MovieCard";
import Pagination from "@/components/base/Pagination";
import { movies } from "@/mocks/movies";
import { usePageMeta } from "@/hooks/usePageMeta";
import FilterBar from "@/pages/category/components/FilterBar";
import CategorySidebar from "@/pages/category/components/CategorySidebar";

const PAGE_SIZE = 8;

const typeConfig: Record<string, { name: string; desc: string }> = {
  movie: {
    name: "国产电影",
    desc: "汇集剧情、动作、悬疑、科幻、历史等多题材国产影片，支持按类型、年份、地区筛选。",
  },
  tvseries: {
    name: "国产电视剧",
    desc: "涵盖家庭、都市、悬疑、古装、科幻等国产剧集，支持按类型、年份、地区筛选。",
  },
};

export default function Category() {
  const params = useParams();
  const type = params.type || "movie";
  const config = typeConfig[type];

  const [genre, setGenre] = useState("全部");
  const [year, setYear] = useState("全部");
  const [region, setRegion] = useState("全部");
  const [sort, setSort] = useState("updated");
  const [page, setPage] = useState(1);

  usePageMeta(
    config ? `国产久久久 - ${config.name}分类` : "分类 - 国产久久久",
    config
      ? `${config.name}分类页，${config.desc}`
      : "国产久久久影视分类页面。",
    "国产久久久,国产影视,分类,筛选"
  );

  const baseList = useMemo(
    () => movies.filter((m) => m.type === type),
    [type]
  );

  const genres = useMemo(() => {
    const set = new Set<string>();
    baseList.forEach((m) => m.genres.forEach((g) => set.add(g)));
    return Array.from(set);
  }, [baseList]);

  const years = useMemo(() => {
    const set = new Set<number>();
    baseList.forEach((m) => set.add(m.year));
    return Array.from(set).sort((a, b) => b - a);
  }, [baseList]);

  const regions = useMemo(() => {
    const set = new Set<string>();
    baseList.forEach((m) => set.add(m.region));
    return Array.from(set);
  }, [baseList]);

  const filtered = useMemo(() => {
    let list = baseList.filter((m) => {
      const matchGenre = genre === "全部" || m.genres.includes(genre);
      const matchYear = year === "全部" || m.year === Number(year);
      const matchRegion = region === "全部" || m.region === region;
      return matchGenre && matchYear && matchRegion;
    });

    if (sort === "hot") {
      list = [...list].sort((a, b) => b.hot - a.hot);
    } else if (sort === "rating") {
      list = [...list].sort((a, b) => b.rating - a.rating);
    } else {
      list = [...list].sort((a, b) => (a.updatedAt < b.updatedAt ? 1 : -1));
    }
    return list;
  }, [baseList, genre, year, region, sort]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pageList = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  const updateFilter = (setter: (v: string) => void) => (value: string) => {
    setter(value);
    setPage(1);
  };

  const resetFilters = () => {
    setGenre("全部");
    setYear("全部");
    setRegion("全部");
    setSort("updated");
    setPage(1);
  };

  if (!config) {
    return (
      <div className="w-full px-4 md:px-8 py-20 text-center">
        <span className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-100 text-foreground-400 mb-5">
          <i className="ri-error-warning-line text-3xl"></i>
        </span>
        <h1 className="font-heading text-xl font-semibold text-foreground-950">
          分类不存在
        </h1>
        <p className="mt-2 text-sm text-foreground-500">
          您访问的分类暂时无法找到，请返回首页重新选择。
        </p>
        <Link
          to="/"
          className="inline-block mt-6 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm whitespace-nowrap cursor-pointer hover:bg-primary-600 transition-colors"
        >
          返回首页
        </Link>
      </div>
    );
  }

  return (
    <div className="w-full">
      <div className="bg-background-100 border-b border-background-200">
        <div className="w-full px-4 md:px-8 py-6 md:py-8">
          <Breadcrumb
            items={[
              { label: "首页", path: "/" },
              { label: "分类", path: "/category/movie" },
              { label: config.name },
            ]}
          />
          <h1 className="mt-3 font-heading text-2xl md:text-3xl font-bold text-foreground-950">
            {config.name}
          </h1>
          <p className="mt-2 text-sm text-foreground-600 max-w-2xl leading-relaxed">
            {config.desc}
          </p>
        </div>
      </div>

      <div className="w-full px-4 md:px-8 py-8 md:py-10">
        <div className="flex flex-col lg:flex-row gap-6 lg:gap-8">
          <CategorySidebar
            type={type}
            activeGenre={genre}
            onGenre={updateFilter(setGenre)}
          />

          <div className="flex-1 min-w-0">
            <FilterBar
              genres={genres}
              years={years}
              regions={regions}
              genre={genre}
              year={year}
              region={region}
              sort={sort}
              resultCount={filtered.length}
              onGenre={updateFilter(setGenre)}
              onYear={updateFilter(setYear)}
              onRegion={updateFilter(setRegion)}
              onSort={(value) => {
                setSort(value);
                setPage(1);
              }}
              onReset={resetFilters}
            />

            {pageList.length > 0 ? (
              <>
                <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-5">
                  {pageList.map((movie) => (
                    <MovieCard key={movie.id} movie={movie} />
                  ))}
                </div>
                <Pagination
                  currentPage={safePage}
                  totalPages={totalPages}
                  onPageChange={setPage}
                />
              </>
            ) : (
              <div className="mt-6 py-20 text-center rounded-lg bg-background-50 border border-background-200">
                <span className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-100 text-foreground-400 mb-5">
                  <i className="ri-inbox-line text-3xl"></i>
                </span>
                <h2 className="font-heading text-lg font-semibold text-foreground-950">
                  暂无符合条件的影视作品
                </h2>
                <p className="mt-2 text-sm text-foreground-500">
                  试试调整筛选条件，或重置筛选浏览全部内容。
                </p>
                <button
                  type="button"
                  onClick={resetFilters}
                  className="mt-6 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm whitespace-nowrap cursor-pointer hover:bg-primary-600 transition-colors"
                >
                  重置筛选
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}